import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120SchM3Service } from './f1120-sch-m3.service';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';

@Component({
    selector: 'gtw-f1120-sch-m3',
    templateUrl: './f1120-sch-m3.component.html',
    styleUrls: ['./f1120-sch-m3.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SchM3Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // Component properties migrated from F1120ScheduleM3Ctrl.js
  modalTitle = "Part I Financial Information and Net Income (Loss) Reconciliation";
  originalData: any = {};
  userSettings: any;
  logged_in_user: string = '';
  leId: string = '';
  formName = '1120PC';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  filing_key: string = '';
  group_type: string = '';
  e_type: string = '';
  systemFlag = 'D';
  showFormLoader = true;
  crudLoading = false;
  isSaveClicked = false;
  
  // Entity information
  entityList: any[] = [];

  // Form data structures (maintaining AngularJS structure)
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  
  scheduleM3FormView: { rows: any[] } = { rows: [] };
  mainData: any[] = [];
  partnerData: any[] = [];

  // Tab configuration (maintaining AngularJS structure)
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;
  currentTabName = "Form Entry";
  
  // Modal minimization state
  minimise = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SchM3Service: F1120SchM3Service,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      // 🔴 CRITICAL: Only proceed if we have row data - NO MOCKS!
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.initSchM3();
      }
    } catch (e) {
      console.error('Error in F1120SchM3 init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
    this.filing_key = this.rowData.CONSOL_GROUP_KEY;
    this.group_type = this.rowData.CONSOL_GROUP_TYPE;
    this.e_type = this.rowData.ENTITY_TYPE;
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  // Initialize Schedule M3 form
  initSchM3() {
    this.fetchSchM3records();
  }

  // 🔴 CRITICAL: API calls use REAL rowData - NO globalParams or 'BASE'!
  fetchSchM3records() {
    const params = { 
      "action_id": 31296, 
      "tax_year": this.rowData.TAX_YEAR,           // NOT hardcoded year
      "scenario": this.rowData.SCENARIO,           // NOT 'BASE'
      "jcd_key": this.rowData.JCD_KEY,            // NOT globalParams.jcd_key
      "combination_key": this.rowData.COMBINATION_KEY,
      "schedule_name": "M3", 
      "part_no": "P1",
      "filing_key": this.filing_key,
      "group_type": this.group_type
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    const scheduleM3Form: { rows: any[] } = { rows: [] };
    let saveParamDtls: any[] = [];
    let tempDataObj: any = {};

    for (let i = 0; i < data.irsForm.formItemList.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
      const rows = data.irsForm.formItemList[i].dataRowList[0].dataCellList.length;
      
      tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[0].name;
      tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[1].name;
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      
      if (rows > 2) {
        this.iterateArray(rows, data.irsForm.formItemList[i].dataRowList[0].dataCellList, tempDataObj);
      }
      
      scheduleM3Form.rows.push(tempDataObj);
    }

    // Set header values
    if (scheduleM3Form.rows.length > 0) {
      scheduleM3Form.rows[0].value = this.rowData.LE_NAME;
      scheduleM3Form.rows[0].value_user_input = this.rowData.EIN;
    }
    
    this.originalData = _.cloneDeep(scheduleM3Form);
    this.scheduleM3FormView = scheduleM3Form;
    this.initFormView(scheduleM3Form);
    
    if (this.filing_key !== '1') {
      this.validateLines();
    }
  }

  // Process different column counts in form data (migrated from AngularJS)
  private iterateArray(count: number, array: any[], tempDataObj: any) {
    if (this.e_type === 'REG') {
      if (count === 3) {
        tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
        tempDataObj.attrib_name_a = array[2].key.split('~')[0];
        tempDataObj.attrib_order_a = array[2].key.split('~')[2];
        tempDataObj.formula = array[2].formula;
        
        if (array[2].value == null) {
          tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, null);
        } else if (array[2].value === "true") {
          tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, "true");
        } else if (array[2].value === "false") {
          tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, "false");
        } else if (array[2].value === "0") {
          tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, "0");
        } else if (array[2].value === 'NA') {
          tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, "NA");
        } else {
          if (tempDataObj.num === "2ab" || tempDataObj.num === "2ae") {
            // Handle date values - use parseAsLocalDate to avoid timezone shift issues
            console.log(`📅 Loading date for ${tempDataObj.num}:`, array[2].value);
            tempDataObj.value = parseAsLocalDate(array[2].value);
          } else if (tempDataObj.num === "3b" || tempDataObj.num === "3c") {
            tempDataObj.value = array[2].value;
          } else {
            // Handle numeric values - ensure proper parsing
            console.log(`🔢 Loading number for ${tempDataObj.num}:`, array[2].value);
            if (array[2].value !== null && array[2].value !== undefined && array[2].value !== '') {
              tempDataObj.value = parseInt(array[2].value) || 0;
            } else {
              tempDataObj.value = 0;
            }
          }
        }
        
        if (tempDataObj.num === 'D') {
          tempDataObj.JSON_DATA = this.partnerData;
        }
      } else {
        this.handleMultiColumnREG(tempDataObj, array);
      }
    } else {
      this.handleNonREGLogic(count, array, tempDataObj);
    }
  }

  private handleMultiColumnREG(tempDataObj: any, array: any[]) {
    if (tempDataObj.num === "B" || tempDataObj.num === "C" || tempDataObj.num === "4b5") {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.value = array[2].value;
      
      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];
      tempDataObj.value_user_input = array[3].value;
      
      if (tempDataObj.value == null) {
        tempDataObj.value = null; // no initial selection
        tempDataObj.conditionalField = true;
      } else if (tempDataObj.value === "true") {
          tempDataObj.value = true;
          tempDataObj.conditionalField = false;
      } else if (tempDataObj.value === "false") {
        // map backend 'false' to null so UI shows no radio selected initially
        tempDataObj.value = null;
        tempDataObj.conditionalField = true;
        } else {
        tempDataObj.value = null; // unknown -> no selection
          tempDataObj.conditionalField = true;
        }
    } else if (tempDataObj.num === "1a" || tempDataObj.num === "1b" || tempDataObj.num === "1c" || tempDataObj.num === "3a" || tempDataObj.num === "2b" || tempDataObj.num === "2c") {
      // CRITICAL: Handle special Yes/No logic from AngularJS
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];

      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];

      // Special logic: array[2] = YES checkbox, array[3] = NO checkbox
      if (array[2].value === "true") {
        tempDataObj.value = true;
        console.log(`✅ Field ${tempDataObj.num}: YES checkbox is checked`);
      } else if (array[3].value === "true") {
        tempDataObj.value = false;
        console.log(`❌ Field ${tempDataObj.num}: NO checkbox is checked`);
      } else {
        tempDataObj.value = 'NA';
        console.log(`⚪ Field ${tempDataObj.num}: No checkbox is checked (NA)`);
      }
    } else {
      // Handle other multi-column cases
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.value = array[2].value;
      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];
      tempDataObj.value_user_input = array[3].value;
    }
  }

  private handleNonREGLogic(count: number, array: any[], tempDataObj: any) {
    if (count === 3) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, array[2].value);
      
      if (tempDataObj.num === 'D') {
        tempDataObj.JSON_DATA = this.partnerData;
      }
    } else {
      if (tempDataObj.num === "B" || tempDataObj.num === "C" || tempDataObj.num === "4b4") {
        tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
        tempDataObj.attrib_name_a = array[2].key.split('~')[0];
        tempDataObj.attrib_order_a = array[2].key.split('~')[2];
        tempDataObj.value = array[2].value;
        
        tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
        tempDataObj.attrib_name_b = array[3].key.split('~')[0];
        tempDataObj.attrib_order_b = array[3].key.split('~')[2];
        tempDataObj.value_user_input = array[3].value;
        
        // Handle conditional field logic like AngularJS
        if (tempDataObj.value == null) {
          tempDataObj.value = false;
          tempDataObj.conditionalField = true;
        } else if (tempDataObj.value === "true") {
          tempDataObj.value = true;
          tempDataObj.conditionalField = false;
        } else if (tempDataObj.value === "false") {
          tempDataObj.value = false;
          tempDataObj.conditionalField = true;
        } else {
          tempDataObj.value = false;
          tempDataObj.conditionalField = true;
        }
      } else if (tempDataObj.num === "1a" || tempDataObj.num === "1b" || tempDataObj.num === "1c" || tempDataObj.num === "3a" || tempDataObj.num === "2b" || tempDataObj.num === "2c") {
        // CRITICAL: Handle special Yes/No logic for NON-REG (same as REG)
        tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
        tempDataObj.attrib_name_a = array[2].key.split('~')[0];
        tempDataObj.attrib_order_a = array[2].key.split('~')[2];

        tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
        tempDataObj.attrib_name_b = array[3].key.split('~')[0];
        tempDataObj.attrib_order_b = array[3].key.split('~')[2];

        // Special logic: array[2] = YES checkbox, array[3] = NO checkbox
        if (array[2].value === "true") {
          tempDataObj.value = true;
          console.log(`✅ NON-REG Field ${tempDataObj.num}: YES checkbox is checked`);
        } else if (array[3].value === "true") {
          tempDataObj.value = false;
          console.log(`❌ NON-REG Field ${tempDataObj.num}: NO checkbox is checked`);
        } else {
          tempDataObj.value = 'NA';
          console.log(`⚪ NON-REG Field ${tempDataObj.num}: No checkbox is checked (NA)`);
        }
      } else {
        // Handle other cases
        tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
        tempDataObj.attrib_name_a = array[2].key.split('~')[0];
        tempDataObj.attrib_order_a = array[2].key.split('~')[2];
        tempDataObj.value = array[2].value;
        tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
        tempDataObj.attrib_name_b = array[3].key.split('~')[0];
        tempDataObj.attrib_order_b = array[3].key.split('~')[2];
        tempDataObj.value_user_input = array[3].value;
      }
    }
  }

  private getCheckBoxValue(rowNum: string, val: any): any {
    console.log(`🔍 DEBUG getCheckBoxValue: rowNum=${rowNum}, val=${val}, type=${typeof val}`);

    const isPrimaryYesNoRow = (n: string) => (
      n === 'A' || n === 'B' || n === 'C' || n === 'D' || n === 'E'
    );

    if (this.e_type === 'REG') {
      if (isPrimaryYesNoRow(rowNum) || rowNum === '4b1' || rowNum === '4b2' || rowNum === '4b3' || rowNum === '4b4') {
        // FIXED: Preserve backend boolean values to maintain radio selection state
        if (val === 'true' || val === true) {
          console.log(`✅ Returning TRUE for rowNum=${rowNum}`);
          return true;
        } else if (val === 'false' || val === false) {
          console.log(`❌ Returning FALSE for rowNum=${rowNum}`);
          return false; // FIXED: Keep false value to show radio selection
        } else {
          console.log(`⚪ Returning NULL for rowNum=${rowNum}`);
          return null; // No initial selection for null/unknown values
        }
      } else if (rowNum === '2ab' || rowNum === '2ae' || rowNum === '3b' || rowNum === '3c') {
        return null;
      } else {
        return 0;
      }
    } else {
      if (isPrimaryYesNoRow(rowNum)) {
        if (val === 'true' || val === true) {
          console.log(`✅ NON-REG Returning TRUE for rowNum=${rowNum}`);
          return true;
        } else if (val === 'false' || val === false) {
          console.log(`❌ NON-REG Returning FALSE for rowNum=${rowNum}`);
          return false; // FIXED: Keep false value to show radio selection
        } else {
          console.log(`⚪ NON-REG Returning NULL for rowNum=${rowNum}`);
          return null;
        }
      } else if (rowNum === '2ab' || rowNum === '2ae' || rowNum === '3b' || rowNum === '3c') {
        return null;
      } else {
        return 0;
      }
    }
  }

  private validateLines() {
    // Implement validation logic as needed
    // This is a placeholder for the complex validation from AngularJS
  }

  // Convert attribute value to proper format for backend (like original AngularJS)
  private getAttributeValue(value: any): string {
    console.log('🔄 getAttributeValue called with:', value, 'type:', typeof value, 'instanceof Date:', value instanceof Date);
    
    // Handle boolean values - convert to string like original AngularJS
    if (value === true) {
      return 'true';
    } else if (value === false) {
      return 'false';
    } else if (value === 'NA') {
      return 'false';
    } else if (value && typeof value === 'object' && value instanceof Date) {
      // Handle dates - format them in YYYY-MM-DD format for backend consistency
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}`;
      console.log('📅 Date formatted:', formatted);
      return formatted;
    } else if (typeof value === 'number') {
      // Ensure numbers are converted to string properly
      console.log('🔢 Number value:', value);
      return value.toString();
    } else if (value !== null && value !== undefined && value !== '') {
      console.log('📝 String/other value:', value);
      return value.toString();
    } else {
      console.log('⚪ Empty/null value');
      return '';
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build save data with real rowData values (like F7004 pattern)
    const tempArray: any[] = [];
    
    for (let i = 0; i < this.scheduleM3FormView.rows.length; i++) {
      const row = this.scheduleM3FormView.rows[i];
      
      const formRowData: any = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: this.getAttributeValue(row.value),
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: 'Y'  // FIXED: Mark as dirty to ensure save
      };
      
      tempArray.push(formRowData);

      // Handle secondary attributes if they exist
      if (typeof row.attrib_name_b !== "undefined") {
        let attributeValueB = '';
        
        // Special logic for Yes/No fields like AngularJS (1a, 1b, 1c, etc.)
        if (row.num === "1a" || row.num === "1b" || row.num === "1c" || row.num === "3a" || row.num === "2b" || row.num === "2c") {
          // CRITICAL: Use inverted logic for NO checkbox (attrib_name_b)
          // AngularJS: value === true ? 'false' : value == 'NA' ? 'false' : value === false ? 'true' : ...
          if (row.value === true) {
            attributeValueB = 'false';  // YES selected → NO checkbox = false
          } else if (row.value === false) {
            attributeValueB = 'true';   // NO selected → NO checkbox = true
          } else {
            attributeValueB = 'false';  // NA → NO checkbox = false
          }
          console.log(`🔄 Field ${row.num}: value=${row.value}, NO checkbox (attrib_name_b)=${attributeValueB}`);
        } else {
          // For other fields (B, C, 4b5, 12a-12d), use value_user_input
          attributeValueB = row.value_user_input || '';
        }
        
        const formRowDataB: any = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_b,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKey_b,
          Attribute_Name: row.attrib_name_b,
          Attribute_Value: attributeValueB,
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: 'Y'  // FIXED: Mark as dirty to ensure save
        };
        tempArray.push(formRowDataB);
      }
    }

    this.formData.editrows = [tempArray];
    this.mainData = [this.formData];

    console.log('💾 SAVE DEBUG - tempArray length:', tempArray.length);
    console.log('💾 SAVE DEBUG - tempArray sample:', tempArray.slice(0, 2));
    console.log('💾 SAVE DEBUG - mainData structure:', this.mainData);

    // 🚀 CRITICAL: Pass filterParams and rowData to service (F7004 pattern)
    this.f1120SchM3Service.saveF1120SchM3Form(
      this.GENERAL_CONFIG.base_url,  // 🚀 Use base_url (contains /gtw/)
      tempArray,  // FIXED: Pass tempArray directly instead of this.mainData
      this.globalService,
      this.filterParams,  // 🚀 Pass dynamic filter params
      this.rowData        // 🚀 Pass row data
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        console.log('💾 SAVE RESPONSE:', response);
        console.log('💾 callSuccess:', response.callSuccess, 'errorMessage:', response.errorMessage);
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string (F7004 pattern)
          this.gtwSnackbar.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        } else {
          this.isSaveClicked = false;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbar.openSnackBar('Error occurred in saving the Transaction. If this continues please contact support', 'gtw-snackbar--red', true, 4);
          }
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.markForCheck();
      }
    );
  }

  // private getAttributeValue(row: any): string {
  //   if (row.value === true) return 'true';
  //   if (row.value === false) return 'false';
  //   if (row.value === 'NA') return 'false';
  //   if (row.value instanceof Date) return row.value.toISOString();
  //   return row.value ? row.value.toString() : '';
  // }

  reset() {
    this.scheduleM3FormView = _.cloneDeep(this.originalData);
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  private initFormView(filteredFormData: any) {
    this.scheduleM3FormView = filteredFormData;
  }

  // Field visibility handler for conditional fields
  fieldVisibility(index: number, state: boolean, name: string) {
    // Update the value and conditional field logic
    if (this.scheduleM3FormView.rows[index]) {
      // Set the actual boolean value based on selection
      this.scheduleM3FormView.rows[index].value = state;
      this.scheduleM3FormView.rows[index].conditionalField = !state;
      
      // Reset value if 'no' is selected for specific rows
      if ((name === 'no' && !state) || (name === 'yes' && state)) {
        if (this.scheduleM3FormView.rows[index].num === 'B' || 
            this.scheduleM3FormView.rows[index].num === 'C') {
          this.scheduleM3FormView.rows[index].value_user_input = '';
        }
      }
    }
  }

  // Update radio value for non-REG entities
  updateRadioValue(row: any, value: boolean) {
    row.value = value;
    row.conditionalField = !value;
    if (row.num === 'B' || row.num === 'C') {
      row.value_user_input = '';
    }
  }

  // Set radio value with proper boolean conversion
  setRadioValue(row: any, value: boolean) {
    row.value = value;
    // Make sure it's a strict boolean, not string
    if (value === true) {
      row.value = true;
    } else if (value === false) {
      row.value = false;
    }
  }

  // Template helper methods for REG entity type
  shouldHideRow(row: any, index: number): boolean {
    // Logic to determine which rows to hide for REG entities
    // This depends on the specific business rules from AngularJS
    return false; // Simplified - show all rows for now
  }

  isCheckboxRow(row: any, index: number): boolean {
    // Determine if this row should show checkbox inputs
    const checkboxRows = ['A', 'B', 'C', 'D', 'E'];
    return checkboxRows.includes(row.num);
  }

  isDateRow(row: any, index: number): boolean {
    // Determine if this row should show date inputs
    return row.num === '2' || (index >= 15 && index <= 17); // Based on AngularJS template
  }

  isTextInputRow(row: any, index: number): boolean {
    // Determine if this row should show text inputs
    const textRows = ['3b', '3c', 'CORP'];
    return textRows.includes(row.num);
  }

  isNumberInputRow(row: any, index: number): boolean {
    // Determine if this row should show number inputs
    // Based on the getCheckBoxValue logic in AngularJS
    return !this.isCheckboxRow(row, index) && !this.isDateRow(row, index) && !this.isTextInputRow(row, index);
  }

  getYesDescription(index: number): string {
    // Get the "Yes" description for checkbox rows
    if (this.scheduleM3FormView.rows[index + 1]) {
      return this.scheduleM3FormView.rows[index + 1].description || '';
    }
    return '';
  }

  getNoDescription(index: number): string {
    // Get the "No" description for checkbox rows
    if (this.scheduleM3FormView.rows[index + 2]) {
      return this.scheduleM3FormView.rows[index + 2].description || '';
    }
    return '';
  }

  getStartDateDescription(index: number): string {
    // Get start date description
    if (this.scheduleM3FormView.rows[index + 1]) {
      return this.scheduleM3FormView.rows[index + 1].description || '';
    }
    return '';
  }

  getEndDateDescription(index: number): string {
    // Get end date description
    if (this.scheduleM3FormView.rows[index + 2]) {
      return this.scheduleM3FormView.rows[index + 2].description || '';
    }
    return '';
  }

  getStartDateValue(index: number): any {
    if (this.scheduleM3FormView.rows[index + 1]) {
      const value = this.scheduleM3FormView.rows[index + 1].value;
      if (value instanceof Date) {
        // Convert Date to YYYY-MM-DD format for input[type="date"]
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return value;
    }
    return null;
  }

  getEndDateValue(index: number): any {
    if (this.scheduleM3FormView.rows[index + 2]) {
      const value = this.scheduleM3FormView.rows[index + 2].value;
      if (value instanceof Date) {
        // Convert Date to YYYY-MM-DD format for input[type="date"]
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return value;
    }
    return null;
  }

  // Template helper methods for Non-REG entity type
  shouldHideRowNonREG(row: any, index: number): boolean {
    // Logic to determine which rows to hide for non-REG entities
    return false; // Simplified - show all rows for now
  }

  isSimpleInputRow(row: any, index: number): boolean {
    // Determine if this row should show simple text inputs for non-REG
    return !this.isCheckboxRowNonREG(row, index);
  }

  isCheckboxRowNonREG(row: any, index: number): boolean {
    // Determine if this row should show checkbox inputs for non-REG
    const checkboxRows = ['A', 'B', 'C', 'D', 'E'];
    return checkboxRows.includes(row.num);
  }



  // Update methods for template bindings
  updatePartnershipName(value: string) {
    if (this.scheduleM3FormView.rows && this.scheduleM3FormView.rows[0]) {
      this.scheduleM3FormView.rows[0].value = value;
    }
  }

  updateEmployerIdNumber(value: string) {
    if (this.scheduleM3FormView.rows && this.scheduleM3FormView.rows[0]) {
      this.scheduleM3FormView.rows[0].value_user_input = value;
    }
  }

  updateStartDateValue(index: number, value: any) {
    if (this.scheduleM3FormView.rows[index + 1]) {
      // Use parseAsLocalDate to avoid timezone shift issues
      if (value && typeof value === 'string') {
        this.scheduleM3FormView.rows[index + 1].value = parseAsLocalDate(value);
        console.log('📅 Updated start date:', value, '-> Date object:', this.scheduleM3FormView.rows[index + 1].value);
      } else {
        this.scheduleM3FormView.rows[index + 1].value = value;
      }
    }
  }

  updateEndDateValue(index: number, value: any) {
    if (this.scheduleM3FormView.rows[index + 2]) {
      // Use parseAsLocalDate to avoid timezone shift issues
      if (value && typeof value === 'string') {
        this.scheduleM3FormView.rows[index + 2].value = parseAsLocalDate(value);
        console.log('📅 Updated end date:', value, '-> Date object:', this.scheduleM3FormView.rows[index + 2].value);
      } else {
        this.scheduleM3FormView.rows[index + 2].value = value;
      }
    }
  }



  // Yes/No checkbox change handler
  onCheckboxChange(index: number, event: Event, type: string): void {
    const target = event.target as HTMLInputElement;
    if (this.scheduleM3FormView.rows[index]) {
      if (type === 'single') {
        // Handle single checkbox like AngularJS: ng-model="row.value"
        this.scheduleM3FormView.rows[index].value = target.checked;
        this.fieldVisibility(index, target.checked, '');
      } else {
        // Handle yes/no checkboxes
        this.scheduleM3FormView.rows[index].value = type === 'yes' ? target.checked : !target.checked;
        this.fieldVisibility(index, this.scheduleM3FormView.rows[index].value, type);
      }
    }
  }

  // Input value update handler
  updateValue(index: number, event: Event, field: string): void {
    const target = event.target as HTMLInputElement;
    if (this.scheduleM3FormView.rows[index]) {
      if (field === 'value') {
        this.scheduleM3FormView.rows[index].value = target.value;
      } else if (field === 'value_user_input') {
        this.scheduleM3FormView.rows[index].value_user_input = target.value;
      }
    }
  }

  // Update value field for dynamic rows
  updateValueField(index: number, event: Event, field: string): void {
    const target = event.target as HTMLInputElement;
    if (this.scheduleM3FormView.rows[index]) {
      if (field === 'value_user_input') {
        this.scheduleM3FormView.rows[index].value_user_input = target.value;
      }
    }
  }

  // Format number with commas
  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    // Convert to number if it's a string
    const num = typeof value === 'string' ? parseFloat(value) : value;
    
    // Check if it's a valid number
    if (isNaN(num)) {
      return value.toString();
    }
    
    // Format number with commas every 3 digits
    return num.toLocaleString('en-US');
  }

  // Update number field with formatting
  updateNumberField(index: number, event: Event, field: string): void {
    const target = event.target as HTMLInputElement;
    let value = target.value.replace(/,/g, ''); // Remove existing commas
    
    if (this.scheduleM3FormView.rows[index]) {
      if (field === 'value') {
        // Store the raw number value
        this.scheduleM3FormView.rows[index].value = value ? parseFloat(value) : null;
      } else if (field === 'value_user_input') {
        this.scheduleM3FormView.rows[index].value_user_input = value ? parseFloat(value) : null;
      }
      
      // Format the displayed value with commas
      setTimeout(() => {
        if (value && !isNaN(parseFloat(value))) {
          target.value = this.formatNumber(parseFloat(value));
        }
      }, 0);
    }
  }

  // Date update handler
  updateDateValue(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.scheduleM3FormView.rows[index] && target.value) {
      // Use parseAsLocalDate to avoid timezone shift issues
      this.scheduleM3FormView.rows[index].value = parseAsLocalDate(target.value);
    }
  }

  // Date formatting for HTML date inputs
  getFormattedDateValue(index: number): string {
    if (this.scheduleM3FormView.rows[index]?.value instanceof Date) {
      const date = this.scheduleM3FormView.rows[index].value;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return this.scheduleM3FormView.rows[index]?.value || '';
  }

  // Partner table management
  addPartnerRow(): void {
    // Find the row with num='D' dynamically
    const rowD = this.scheduleM3FormView.rows.find(row => row.num === 'D');
    if (rowD) {
      if (!rowD.JSON_DATA) {
        rowD.JSON_DATA = [];
      }
      rowD.JSON_DATA.push({
        DATA_ATTRIB_A: '',
        DATA_ATTRIB_B: '',
        DATA_ATTRIB_C: ''
      });
    }
  }

  removePartnerRow(index: number): void {
    // Find the row with num='D' dynamically
    const rowD = this.scheduleM3FormView.rows.find(row => row.num === 'D');
    if (rowD?.JSON_DATA) {
      rowD.JSON_DATA.splice(index, 1);
    }
  }

  // Check if row should show right column
  shouldShowRightColumn(row: any): boolean {
    // Show for all rows in the range, based on original conditions
    return true; // Simplify for now, can add filing_key logic later
  }

  // Check if row is special (should hide right column content)
  isSpecialRow(rowNum: string): boolean {
    const specialRows = ['12', '4b', '4b1', '4b2', '4b3', '4b4', '4b5'];
    return specialRows.includes(rowNum);
  }

  // Check if row should have numeric input in right column
  isNumericInputRow(rowNum: string): boolean {
    const numericRows = ['11', '4a', '5a', '5b', '6a', '6b', '7a', '7b', '7c', '8', '9', '10a', '10b', '10c'];
    return numericRows.includes(rowNum);
  }

  // Compute values (placeholder for complex calculations)
  computeValues(): void {
    // Implement calculation logic as needed
    console.log('Computing values...');
  }

  // Track by function for ngFor performance
  itemTracker(index: number, item: any): any {
    return item ? item.lineAttrKey_a || item.LEID || index : index;
  }

  // Handle checkbox behavior like AngularJS ng-true-value/ng-false-value
  onSingleCheckboxChange(index: number, targetValue: boolean, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    const currentValue = this.scheduleM3FormView.rows[index]?.value;
    
    console.log(`🔄 onSingleCheckboxChange: index=${index}, targetValue=${targetValue}, isChecked=${isChecked}, currentValue=${currentValue}`);
    
    if (isChecked) {
      // Checkbox is being checked - set to target value
      this.scheduleM3FormView.rows[index].value = targetValue;
      console.log(`✅ Set value to ${targetValue} for index ${index}`);
      // Call fieldVisibility like AngularJS
      this.fieldVisibility(index, targetValue, targetValue ? 'yes' : 'no');
    } else {
      // Checkbox is being unchecked - set to null
      this.scheduleM3FormView.rows[index].value = null;
      console.log(`🔄 Unchecked: Reset value to null for index ${index}`);
    }
  }
}